/**
 * SSAOPass.ts — full-screen pass producing a single-channel AO factor.
 *
 * Reads the shared depth buffer (rt.current.depthView) and writes an R8
 * texture matching the canvas resolution. BlitPass samples the AO texture
 * and multiplies the HDR colour before tonemapping.
 *
 * Recreates its target + bind group on RT resize.
 */

import wgsl from "../shaders/ssao.wgsl?raw";
import { createUniformBuffer } from "../gpu/Buffers";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { Camera } from "../Camera";

const STRUCT_SIZE = 32; // 8 × f32

export interface SSAOPassOptions {
  /** World-space sampling radius (m). Default 1.0. */
  radius?: number;
  /** Depth bias (m) to avoid self-occlusion. Default 0.05. */
  bias?: number;
  /** AO contrast multiplier. Default 1.6. */
  intensity?: number;
  /** Camera distance past which AO fades to 1. Default 600 m. */
  maxDistance?: number;
}

export class SSAOPass implements RenderPass {
  readonly name = "SSAOPass";

  readonly radius: number;
  readonly bias: number;
  readonly intensity: number;
  readonly maxDistance: number;

  private device: GPUDevice;
  private rt: ManagedRenderTarget;
  private pipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  private uniform: GPUBuffer;

  private aoTex!: GPUTexture;
  private aoView!: GPUTextureView;
  private bindGroup!: GPUBindGroup;
  private unsubscribe: () => void;

  /** Subscribers (BlitPass) get the new view on RT resize. */
  private viewListeners = new Set<(view: GPUTextureView) => void>();

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    private camera: Camera,
    opts: SSAOPassOptions = {},
  ) {
    this.device = device;
    this.rt = rt;
    this.radius = opts.radius ?? 1.0;
    this.bias = opts.bias ?? 0.05;
    this.intensity = opts.intensity ?? 1.1;
    this.maxDistance = opts.maxDistance ?? 400;

    this.uniform = createUniformBuffer(device, STRUCT_SIZE, "SSAOPass.params");

    const module = device.createShaderModule({
      label: "ssao.wgsl",
      code: wgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "SSAOPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "depth" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "SSAOPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      vertex: { module, entryPoint: "vs_main" },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [{ format: "r8unorm" }],
      },
      primitive: { topology: "triangle-list" },
    });

    this.rebuildTarget();
    this.unsubscribe = rt.onRecreate(() => {
      this.rebuildTarget();
      for (const cb of this.viewListeners) cb(this.aoView);
    });
  }

  /** Current AO texture view (R8). */
  get view(): GPUTextureView {
    return this.aoView;
  }

  /** Subscribe to RT-resize (so BlitPass can rebuild its bind group). */
  onResize(cb: (view: GPUTextureView) => void): () => void {
    this.viewListeners.add(cb);
    return () => this.viewListeners.delete(cb);
  }

  private rebuildTarget(): void {
    if (this.aoTex) this.aoTex.destroy();
    const { width, height } = this.rt.current;
    this.aoTex = this.device.createTexture({
      label: "SSAOPass.ao",
      size: { width, height },
      format: "r8unorm",
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.aoView = this.aoTex.createView({ label: "SSAOPass.ao.view" });
    this.bindGroup = this.device.createBindGroup({
      label: "SSAOPass.bg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.camera.buffer } },
        { binding: 1, resource: { buffer: this.uniform } },
        { binding: 2, resource: this.rt.current.depthView },
      ],
    });
  }

  encode(encoder: GPUCommandEncoder, _frame: FrameContext): void {
    const { width, height } = this.rt.current;
    const data = new Float32Array([
      width,
      height,
      this.radius,
      this.bias,
      this.intensity,
      this.maxDistance,
      0,
      0,
    ]);
    this.device.queue.writeBuffer(this.uniform, 0, data);

    const pass = encoder.beginRenderPass({
      label: "SSAOPass",
      colorAttachments: [
        {
          view: this.aoView,
          clearValue: { r: 1, g: 0, b: 0, a: 0 },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3, 1, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.unsubscribe();
    this.viewListeners.clear();
    if (this.aoTex) this.aoTex.destroy();
  }
}
